'use strict';

/**
 * `<gridstack-layout>` — GridStack driver for the patient chart. Elm owns the DOM and the
 * `gs-*` attributes; this element owns geometry only — `layout` in, `layoutchange` out.
 *
 * What the code can't say for itself:
 *
 * - `_idSeq` starts high so our `gs-id-N` stylesheet classes can't collide with Clinical's
 *   separate GridStack bundle in the shared `<head>`.
 * - `columnMax` repeats the column count because the responsive layer otherwise resets the
 *   grid to its own default of 12 above the largest breakpoint. `defaultColumn` needs it
 *   too: the engine only ever raises that above 12, so a narrower grid caches and saves
 *   against a twelve-column layout it never had.
 * - `list` is only right on the collapse; growing back restores the cached wide layout,
 *   which `list` would suppress by re-flowing untouched cards.
 * - `_mount` doesn't apply the layout: `init` already widgetized from the `gs-*` attributes.
 * - `_applyLayout` spaces placements `RANK_GAP` apart so no move collides with one that
 *   hasn't moved yet.
 * - The 1199 breakpoint must track `lg` in gridstack.pcss.
 */

const { GridStack } = require('gridstack');

GridStack.Engine._idSeq = 1000;

const RANK_GAP = 1000;

function chartGridEngine(columnMax) {
  return class extends GridStack.Engine {
    constructor(opts) {
      super(opts);
      this.defaultColumn = columnMax;
    }

    columnChanged(prevColumn, column, layout) {
      return super.columnChanged(prevColumn, column, column > prevColumn ? 'moveScale' : layout);
    }
  };
}

class GridStackLayout extends HTMLElement {
  connectedCallback() {
    if (this.grid) return;
    if (!this.classList.contains('grid-stack')) {
      this.classList.add('grid-stack');
    }
    requestAnimationFrame(() => {
      if (!this.isConnected || this.grid) return;
      this._mount();
    });
  }

  _mount() {
    const options = this._gridOptions();
    this.style.setProperty('--gridstack-outer-margin', `${options.margin}px`);
    this.grid = GridStack.init(options, this);
    this._appliedRevision = this._layout?.revision;
    this._bindGridEvents();
    this._observeChildren();
  }

  _gridOptions() {
    const column = parseInt(this.getAttribute('columns'), 10) || 3;
    return {
      float: false,
      animate: false,
      styleInHead: true,
      resizable: { handles: 'se' },
      alwaysShowResizeHandle: true,
      engineClass: chartGridEngine(column),
      column,
      cellHeight: parseInt(this.getAttribute('cell-height'), 10) || 'auto',
      margin: parseInt(this.getAttribute('margin'), 10) || 0,
      handle: '.grid-stack-grip',
      columnOpts: {
        breakpointForWindow: true,
        layout: 'list',
        columnMax: column,
        breakpoints: [{ w: 1199, c: 1 }],
      },
    };
  }

  _bindGridEvents() {
    this.grid.on('resizestop', () => this._emitLayoutChange());
    this.grid.on('dragstop', () => this._emitLayoutChange());
  }

  set layout(value) {
    this._layout = value;
    this._applyLayout();
  }

  _applyLayout() {
    if (!this.grid || !Array.isArray(this._layout?.widgets)) return;
    if (this._layout.revision !== undefined && this._layout.revision === this._appliedRevision) {
      return;
    }
    this._appliedRevision = this._layout.revision;
    const elements = new Map();
    for (const node of this.querySelectorAll('.grid-stack-item')) {
      elements.set(node.getAttribute('gs-id'), node);
    }
    const ranked = [...this._layout.widgets].sort((a, b) => a.y - b.y || a.x - b.x);
    this.grid.batchUpdate();
    ranked.forEach(({ id, x, w, h }, rank) => {
      const el = elements.get(id);
      if (!el) return;
      this.grid.update(el, { x, y: rank * RANK_GAP, w, h });
    });
    this.grid.batchUpdate(false);
  }

  _emitLayoutChange() {
    const changes = this.grid.save(false).map(({ id, x, y, w, h, minH }) => ({
      id,
      x,
      y,
      w: w ?? 1,
      h: h ?? minH ?? 1,
    }));
    this.dispatchEvent(new CustomEvent('layoutchange', { detail: changes }));
  }

  _observeChildren() {
    this._observer = new MutationObserver((mutations) => {
      if (!this.grid) return;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && node.classList.contains('grid-stack-item') && !node.gridstackNode) {
            this.grid.makeWidget(node);
          }
        }
        for (const node of m.removedNodes) {
          if (
            node.nodeType === 1 &&
            node.classList.contains('grid-stack-item') &&
            !node.isConnected &&
            node.gridstackNode
          ) {
            this.grid.removeWidget(node, false);
          }
        }
      }
    });
    this._observer.observe(this, { childList: true });
  }

  disconnectedCallback() {
    if (this._observer) {
      this._observer.disconnect();
      this._observer = null;
    }
    if (this.grid) {
      this.grid.destroy(false);
      this.grid = null;
    }
    this._appliedRevision = undefined;
  }
}

if (!customElements.get('gridstack-layout')) {
  customElements.define('gridstack-layout', GridStackLayout);
}

module.exports = GridStackLayout;
